import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {addStreamCamera} from '@/entities/stream/api';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {Sheet} from '@/shared/ui/sheet';
import {spacing} from '@/shared/ui/theme';
import {CameraCredentialsCard} from './CameraCredentialsCard';

/**
 * «Камеры» → «Добавить камеру»: our own camera whose firmware takes a pasted-in
 * signalling address and secret. Name → the camera appears in the list at once,
 * and the sheet shows what to paste into it. Closing the sheet loses the secret
 * for good (a new one can be issued from the camera's settings).
 */
const styles = StyleSheet.create({
  body: {gap: spacing(4)},
});

export function AddCameraSheet({
  companyId,
  visible,
  onClose,
}: {
  companyId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const add = useMutation({
    mutationFn: () => addStreamCamera(companyId, name.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
    },
  });

  function close() {
    setName('');
    add.reset();
    onClose();
  }

  return (
    <Sheet
      visible={visible}
      onClose={close}
      title={t('camera.add.title')}
      subtitle={t('camera.add.subtitle')}
      testID="add-camera-sheet">
      <View style={styles.body}>
        {add.data ? (
          <CameraCredentialsCard credentials={add.data} />
        ) : (
          <>
            <AppInput
              label={t('camera.add.name')}
              value={name}
              onChangeText={setName}
              maxLength={128}
              placeholder={t('camera.add.namePlaceholder')}
            />
            {add.isError ? (
              <Banner tone="error" title={messageOf(add.error, t('errors.unknown'))} />
            ) : null}
            <AppButton
              title={t('camera.add.create')}
              disabled={!name.trim()}
              loading={add.isPending}
              onPress={() => add.mutate()}
            />
          </>
        )}
        <AppButton title={t('actions.close')} tone="outline" onPress={close} />
      </View>
    </Sheet>
  );
}
